import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Download, X } from "lucide-react";
import useInstallPrompt from "../hooks/useInstallPrompt";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";
import useCookieConsent from "../hooks/useCookieConsent";

const DISMISSED_KEY = "zenfresh-install-prompt-dismissed";

/** Bottom-left, mirroring EngagementWidgetStack's bottom-right FABs without overlapping them. */
export default function InstallPrompt() {
  const { isInstallable, promptInstall } = useInstallPrompt();
  const { preferences } = useCookieConsent();
  const reducedMotion = usePrefersReducedMotion();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISSED_KEY) === "1");
  const [prompting, setPrompting] = useState(false);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "1");
    setDismissed(true);
  };

  // Guards against a rapid double-click calling .prompt() twice on the same
  // captured BeforeInstallPromptEvent while the first call's await is still
  // pending — same disable-while-in-flight pattern the enquiry forms use.
  const handleInstallClick = async () => {
    setPrompting(true);
    try {
      await promptInstall();
    } finally {
      setPrompting(false);
    }
  };

  const visible = isInstallable && !dismissed;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="region"
          aria-label="Install ZenFresh"
          initial={reducedMotion ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: reducedMotion ? 0 : 0.25 }}
          className={`fixed left-4 z-40 flex max-w-xs items-center gap-3 rounded-2xl bg-white p-4 shadow-lg ring-1 ring-ocean-900/10 transition-[bottom] duration-300 sm:left-6 dark:bg-ocean-900 dark:ring-white/10 ${
            preferences === null ? "bottom-28 sm:bottom-24" : "bottom-6"
          }`}
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-400/15 text-teal-500 dark:bg-teal-400/20 dark:text-teal-300">
            <Download className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-display text-sm font-semibold text-ocean-950 dark:text-foam-50">Install ZenFresh</p>
            <button
              type="button"
              onClick={() => void handleInstallClick()}
              disabled={prompting}
              className="mt-1 text-xs font-semibold text-teal-600 hover:text-teal-700 disabled:opacity-60 dark:text-teal-300"
            >
              Add to your home screen
            </button>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            className="shrink-0 text-ocean-800/40 hover:text-ocean-800/70 dark:text-foam-100/40 dark:hover:text-foam-100/70"
          >
            <X className="h-4 w-4" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
